import { useEffect } from 'react';
import type { MetaConversation } from '@/lib/stores/metaMessagingStore';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { ConversationStatusBadge } from '@/components/whatsapp2/shared/ConversationStatusBadge';
import { OperatorAssignPicker } from '@/components/whatsapp2/shared/OperatorAssignPicker';
import { LabelPicker } from '@/components/whatsapp2/shared/LabelPicker';
import { metaPrefsKey } from './metaListRules';

/**
 * Stato ("Da gestire" / "In gestione" / "Chiusa"), assegnatario ed etichette di una
 * conversazione Instagram o Messenger: gli stessi controlli e la stessa tabella delle
 * chat WhatsApp (whatsapp_chat_meta, whatsapp_chat_label_assignments), con la chiave
 * `meta:<id conversazione>`. Cosi' chi lavora la casella ha gli stessi strumenti su
 * tutti i canali (test della chat del 25/09/2026).
 */
export function metaChatKey(conversationId: string): string {
  return metaPrefsKey(conversationId);
}

export function MetaConversationTools({ conversation }: { conversation: MetaConversation }) {
  const key = metaChatKey(conversation.id);
  const organizationId = conversation.organization_id;
  const chatMeta = useWhatsAppChatMetaStore((s) => s.chatMeta);
  const orgMembers = useWhatsAppChatMetaStore((s) => s.orgMembers);
  const fetchChatMeta = useWhatsAppChatMetaStore((s) => s.fetchChatMeta);
  const fetchChatLabels = useWhatsAppChatMetaStore((s) => s.fetchChatLabels);
  const fetchOrgMembers = useWhatsAppChatMetaStore((s) => s.fetchOrgMembers);
  const upsertMeta = useWhatsAppChatMetaStore((s) => s.upsertMeta);

  useEffect(() => {
    if (!organizationId) return;
    fetchChatMeta([key], organizationId).catch(() => undefined);
    fetchChatLabels([key], organizationId).catch(() => undefined);
    if (useWhatsAppChatMetaStore.getState().orgMembers.length === 0) {
      fetchOrgMembers(organizationId).catch(() => undefined);
    }
  }, [key, organizationId, fetchChatMeta, fetchChatLabels, fetchOrgMembers]);

  // Nuovo messaggio del contatto su una conversazione chiusa: torna "Da gestire" anche
  // a schermo (nel DB lo fa il trigger su meta_messages, migration 20260925c).
  useEffect(() => {
    if (conversation.last_inbound_at) useWhatsAppChatMetaStore.getState().reopenIfClosed(key);
  }, [conversation.last_inbound_at, key]);

  if (!organizationId) return null;
  const meta = chatMeta.get(key);

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <ConversationStatusBadge
        status={meta?.conversation_status || 'da_gestire'}
        onChange={(status) => upsertMeta(key, { conversation_status: status }, organizationId)}
      />
      <OperatorAssignPicker
        assignedOperatorId={meta?.assigned_operator_id || null}
        members={orgMembers}
        onAssign={(userId) =>
          upsertMeta(key, { assigned_operator_id: userId, assigned_at: userId ? new Date().toISOString() : null }, organizationId)
        }
      />
      <LabelPicker chatJid={key} organizationId={organizationId} />
    </div>
  );
}
